"use client";

import {
  Checkbox,
  ErrorNotice,
  Panel,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import type { AUTHOR_AFFILIATION_FORMATTER_STRINGS } from "@/components/tools/author-affiliation-formatter";
import { SmallButton } from "@/components/tools/publishing-statements/kit";
import { t } from "@/lib/i18n/interpolate";
import { parseOrcid } from "@/lib/tool-kit/publishing/affiliations";

/*
 * One author's fields in the affiliation formatter. Layout only: every word comes from
 * the widget's strings.
 */

type Strings = typeof AUTHOR_AFFILIATION_FORMATTER_STRINGS;

export interface AffiliationRow {
  id: number;
  text: string;
}

export interface AuthorRow {
  id: number;
  name: string;
  affiliations: AffiliationRow[];
  orcid: string;
  email: string;
  corresponding: boolean;
  equal: boolean;
}

function OrcidStatus({ value, strings }: { value: string; strings: Strings }) {
  const status = parseOrcid(value).status;
  if (status === "checksum") {
    return <ErrorNotice>{strings.orcidChecksum}</ErrorNotice>;
  }
  if (status === "format") {
    return <ErrorNotice>{strings.orcidFormat}</ErrorNotice>;
  }
  if (status === "valid") {
    return <p className={HINT}>{strings.orcidValid}</p>;
  }
  return null;
}

export function AuthorCard({
  row,
  index,
  count,
  strings,
  nextId,
  onChange,
  onMove,
  onRemove,
}: {
  row: AuthorRow;
  index: number;
  count: number;
  strings: Strings;
  /** Allocates an id for a new affiliation row. */
  nextId: () => number;
  onChange: (patch: Partial<AuthorRow>) => void;
  onMove: (offset: number) => void;
  onRemove: () => void;
}) {
  const number = index + 1;
  return (
    <Panel title={t(strings.authorPanel, { number })}>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id={`aaf-name-${row.id}`}
          label={strings.nameLabel}
          onChange={(name) => onChange({ name })}
          placeholder={strings.namePlaceholder}
          value={row.name}
        />
        <div className="space-y-2">
          <TextField
            id={`aaf-orcid-${row.id}`}
            label={strings.orcidLabel}
            mono
            onChange={(orcid) => onChange({ orcid })}
            placeholder={strings.orcidPlaceholder}
            value={row.orcid}
          />
          <OrcidStatus strings={strings} value={row.orcid} />
        </div>
      </div>
      <div className="space-y-3">
        {row.affiliations.map((affiliation, affiliationIndex) => (
          <div className="flex items-end gap-3" key={affiliation.id}>
            <div className="flex-1">
              <TextField
                id={`aaf-affil-${affiliation.id}`}
                label={t(strings.affiliationLabel, {
                  number: affiliationIndex + 1,
                })}
                onChange={(text) =>
                  onChange({
                    affiliations: row.affiliations.map((item) =>
                      item.id === affiliation.id ? { ...item, text } : item
                    ),
                  })
                }
                placeholder={strings.affiliationPlaceholder}
                value={affiliation.text}
              />
            </div>
            <SmallButton
              ariaLabel={t(strings.removeAffiliationAria, {
                number: affiliationIndex + 1,
                author: number,
              })}
              disabled={row.affiliations.length <= 1}
              label={strings.removeAffiliation}
              onClick={() =>
                onChange({
                  affiliations: row.affiliations.filter(
                    (item) => item.id !== affiliation.id
                  ),
                })
              }
            />
          </div>
        ))}
        <SmallButton
          label={strings.addAffiliation}
          onClick={() =>
            onChange({
              affiliations: [...row.affiliations, { id: nextId(), text: "" }],
            })
          }
        />
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        <Checkbox
          checked={row.corresponding}
          label={strings.correspondingCheckbox}
          onChange={(corresponding) => onChange({ corresponding })}
        />
        <Checkbox
          checked={row.equal}
          label={strings.equalLabel}
          onChange={(equal) => onChange({ equal })}
        />
      </div>
      {row.corresponding ? (
        <TextField
          id={`aaf-email-${row.id}`}
          label={strings.emailLabel}
          onChange={(email) => onChange({ email })}
          placeholder={strings.emailPlaceholder}
          value={row.email}
        />
      ) : null}
      <div className="flex flex-wrap gap-3">
        <SmallButton
          ariaLabel={t(strings.moveUpAria, { number })}
          disabled={index === 0}
          label={strings.moveUp}
          onClick={() => onMove(-1)}
        />
        <SmallButton
          ariaLabel={t(strings.moveDownAria, { number })}
          disabled={index === count - 1}
          label={strings.moveDown}
          onClick={() => onMove(1)}
        />
        <SmallButton
          ariaLabel={t(strings.removeAuthorAria, { number })}
          disabled={count <= 1}
          label={strings.removeAuthor}
          onClick={onRemove}
        />
      </div>
    </Panel>
  );
}
